import React from 'react';

import { CONTRIBUTION_MODES } from '@lumapps/contents/constants';
import { getCurrentContributionMode } from '@lumapps/contents/ducks/selectors';
import { Divider } from '@lumapps/lumx/react';
import { VideoPickerDialog } from '@lumapps/play-pickers/components/VideoPickerDialog';
import { UploadCorporateVideoDialog } from '@lumapps/play-video-library/components/UploadCorporateVideoDialog';
import { UploadingCorporateVideoFormDialog } from '@lumapps/play-video-library/components/UploadingCorporateVideoFormDialog';
import { useCreateVideo } from '@lumapps/play-video-library/hooks/useCreateVideo/useCreateVideo';
import { useGoogleDriveUpload } from '@lumapps/play-video-library/hooks/useGoogleDriveUpload';
import type { GetVideoResult, UploadingVideo, Video } from '@lumapps/play/api/types';
import {
    WIDGET_PLAY_SETTINGS_FIELDS,
    useFetchUniqueVideo,
} from '@lumapps/play/hooks/useFetchUniqueVideo/useFetchUniqueVideo';
import { arrayFromObjectOfEnabledValues } from '@lumapps/play/utils';
import { useSelector } from '@lumapps/redux/react';
import { useTranslate } from '@lumapps/translations';
import { inputLanguageSelector } from '@lumapps/translations/ducks/selectors';
import { useBooleanState } from '@lumapps/utils/hooks/useBooleanState';

import type { WidgetPlayPropertiesType } from '../../types';
import { getVideoIdForLanguage } from '../../utils';
import { WidgetPlayUploader } from '../WidgetPlayUploader';
import { MetadataSelection } from './MetadataSelection';

import './index.scss';

export interface WidgetPlaySettingsProps {
    properties?: WidgetPlayPropertiesType;
    onChange: (properties: WidgetPlayPropertiesType) => void;
}

const CLASSNAME = 'widget-play-settings';

export const WidgetPlaySettings: React.FC<WidgetPlaySettingsProps> = ({ properties, onChange }) => {
    const inputLanguage = useSelector(inputLanguageSelector);
    const { translateObject } = useTranslate();
    const videoId = properties ? getVideoIdForLanguage(properties, translateObject, true, inputLanguage) : undefined;
    const currentContributionMode = useSelector(getCurrentContributionMode);

    const [pickerDialogIsOpen, togglePickerDialog] = useBooleanState(false);
    const [isUploadingDialogOpen, , closeUploadingDialog, openUploadingDialog] = useBooleanState(false);
    const [isUploadDialogOpen, , closeUploadDialog, openUploadDialog] = useBooleanState(false);
    const [createVideoError, setCreateVideoError] = React.useState<string>();
    const [videoFile, setVideoFile] = React.useState<File>();

    const clearFileData = () => {
        setVideoFile(undefined);
    };

    const onCreateVideoSuccess = () => {
        closeUploadDialog();
        openUploadingDialog();
        setCreateVideoError(undefined);
    };

    const { createdVideo, upload, createVideo } = useCreateVideo({
        isHiddenIn: { content: false, gallery: false, search: false },
        defaultStatus: 'published',
        clearFileData,
        onSuccess: onCreateVideoSuccess,
        setCreateVideoError,
    });

    /**
     * /!\ We need to keep the 'video.id' format to ensure compatibility
     * with the mobile team.
     * For now, we keep 'properties.videoId' as a fallback to ensure the availability
     * of the widgets created recently on the web, until the data format of all
     * widgets are manually corrected
     */

    const { videoLoading, video } = useFetchUniqueVideo({ videoId, fields: WIDGET_PLAY_SETTINGS_FIELDS });
    const [savedVideo, setSavedVideo] = React.useState<GetVideoResult>(video);

    const handleSelectionConfirm = (videos: Video[] | undefined) => {
        if (videos && videos.length > 0) {
            // Save as array for future multi-select support (currently single selection)
            const selectedVideoIds = videos.map((video) => video.id);

            onChange({
                ...properties,
                video: {
                    ...(typeof properties?.video === 'object' ? properties.video : {}),
                    [inputLanguage]: {
                        ids: selectedVideoIds,
                    },
                },
            });

            togglePickerDialog();
        }
    };

    React.useEffect(() => {
        setSavedVideo(video);
    }, [video]);

    const resetVideo = () => {
        setSavedVideo(undefined);

        const currentVideo = typeof properties?.video === 'object' ? { ...properties.video } : {};

        delete currentVideo[inputLanguage];

        const hasVideos = Object.keys(currentVideo).some(
            (lang) => currentVideo[lang]?.ids && currentVideo[lang].ids.length > 0,
        );

        onChange({
            video: hasVideos ? currentVideo : undefined,
            videoId: hasVideos ? properties?.videoId : undefined,
            order: undefined,
        });
    };

    /**
     * If the widget was created before the metadata selection feature was shipped
     * we set each keys to true so all metadata are enabled by default
     */
    const visibleMetadata = React.useMemo(
        () => ({
            title: properties?.order?.includes('title') ?? true,
            author: properties?.order?.includes('author') ?? true,
            publicationDate: properties?.order?.includes('publicationDate') ?? true,
            views: properties?.order?.includes('views') ?? true,
            description: properties?.order?.includes('description') ?? true,
            social: properties?.order?.includes('social') ?? false,
            tags: properties?.order?.includes('tags') ?? false,
        }),
        [properties?.order],
    );

    const handleSaveUploadVideo = (video: Video | UploadingVideo) => {
        onChange({
            ...properties,
            video: {
                // Preserve videos in other languages
                ...(typeof properties?.video === 'object' ? properties.video : {}),
                // Set uploaded video for current language as array (single item for now)
                [inputLanguage]: {
                    ids: [video.id],
                },
            },
        });
        closeUploadingDialog();
    };

    // Video upload with Google Drive
    const { isGoogleUser, openGoogleDrivePicker } = useGoogleDriveUpload({
        onFilesSelected: (files) => {
            createVideo(files[0]);
        },
    });

    const handleDiscardUploadDialog = () => {
        closeUploadDialog();
        setCreateVideoError(undefined);
    };

    React.useEffect(() => {
        /**
         * If the widget was created before the metadata selection feature was shipped,
         * when the user picks a video, we call the onChange function with all metadata enabled
         * so the backend can register them properly.
         */
        if (savedVideo && properties?.order === undefined) {
            onChange({ order: arrayFromObjectOfEnabledValues(visibleMetadata) });
        }
    }, [onChange, properties, savedVideo, visibleMetadata]);

    return (
        <>
            <div className={CLASSNAME}>
                <WidgetPlayUploader
                    isGoogleUser={isGoogleUser}
                    loading={videoLoading}
                    openDrivePickerDialog={openGoogleDrivePicker}
                    openPickerDialog={togglePickerDialog}
                    openUploadDialog={openUploadDialog}
                    resetVideo={resetVideo}
                    video={savedVideo}
                />
                {currentContributionMode !== CONTRIBUTION_MODES.WRITER ? (
                    <>
                        <Divider />
                        <MetadataSelection
                            onChange={onChange}
                            visibleMetadata={visibleMetadata}
                            videoLoading={videoLoading}
                        />
                    </>
                ) : null}
            </div>

            <VideoPickerDialog
                isOpen={pickerDialogIsOpen}
                onSelectionCancel={togglePickerDialog}
                onSelectionConfirm={handleSelectionConfirm}
                shouldPickScheduled
            />

            {createdVideo ? (
                <UploadingCorporateVideoFormDialog
                    isOpen={isUploadingDialogOpen}
                    onClose={() => {
                        closeUploadingDialog();
                        clearFileData();
                    }}
                    onSave={handleSaveUploadVideo}
                    videoFile={videoFile}
                    video={createdVideo}
                    upload={upload}
                />
            ) : null}

            <UploadCorporateVideoDialog
                isOpen={isUploadDialogOpen}
                onCancel={handleDiscardUploadDialog}
                onDrop={(file) => {
                    setVideoFile(file);
                    createVideo(file);
                }}
                createVideoError={createVideoError}
            />
        </>
    );
};
